if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.rpDisplayMs = 6000;
        this.rpTimer = null;
        this.rpSlides = [];
        this.rpIndex = 0;
        this.rpTouchStartX = null;

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;
            button.addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              this.rpSyncIndex(mediaToSwitch.dataset.target);
              this.rpRestartTimer();
            });
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.rpInitAutoplay();
      }

      onSlideChanged(event) {
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (mediaId) this.rpSyncIndex(mediaId);

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        this.rpRestartTimer();
      }

      setActiveMedia(mediaId, prepend, viewingMode) {
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        // Native Dawn scrolling only. No artificial hiding or blank transition.
        window.requestAnimationFrame(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const targetLeft = activeMedia.offsetLeft;
            const scroller = activeMedia.parentElement;
            if (scroller) scroller.scrollTo({ left: targetLeft, behavior: 'auto' });
          }
        });

        this.playActiveMedia(activeMedia);
        this.rpSyncIndex(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'auto' });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);

        window.requestAnimationFrame(() => {
          const video = activeItem.querySelector('video');
          if (!video) return;
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.preload = 'auto';
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('preload', 'auto');
          try { video.currentTime = 0; } catch (_) {}
          const promise = video.play();
          if (promise?.catch) promise.catch(() => {});
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.slider.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      rpGetSlides() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('li.product__media-item[data-media-id]')).filter(
          (slide) => slide.closest('media-gallery') === this
        );
      }

      rpSyncIndex(mediaId) {
        const index = this.rpSlides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index >= 0) this.rpIndex = index;
      }

      rpPrepareMedia() {
        this.rpSlides.forEach((slide) => {
          slide.classList.remove('scroll-trigger', 'animate--fade-in');
          slide.querySelectorAll('img').forEach((img) => {
            img.loading = 'eager';
            img.decoding = 'async';
            if (img.currentSrc || img.src) {
              const preload = new Image();
              preload.src = img.currentSrc || img.src;
            }
          });

          const deferred = slide.querySelector('deferred-media');
          if (deferred && !deferred.hasAttribute('loaded') && typeof deferred.loadContent === 'function') {
            try { deferred.loadContent(false); } catch (_) {}
          }

          const video = slide.querySelector('video');
          if (video) {
            video.muted = true;
            video.defaultMuted = true;
            video.loop = true;
            video.playsInline = true;
            video.preload = 'auto';
            video.setAttribute('muted', '');
            video.setAttribute('loop', '');
            video.setAttribute('playsinline', '');
            video.setAttribute('preload', 'auto');
            try { video.load(); } catch (_) {}
          }
        });
      }

      rpShow(index) {
        if (!this.rpSlides.length) return;
        this.rpIndex = (index + this.rpSlides.length) % this.rpSlides.length;
        const slide = this.rpSlides[this.rpIndex];
        if (!slide?.dataset?.mediaId) return;
        this.setActiveMedia(slide.dataset.mediaId, false);
      }

      rpRestartTimer() {
        clearTimeout(this.rpTimer);
        if (document.hidden || this.rpSlides.length < 2) return;
        this.rpTimer = window.setTimeout(() => {
          this.rpShow(this.rpIndex + 1);
          this.rpRestartTimer();
        }, this.rpDisplayMs);
      }

      rpInitAutoplay() {
        this.rpSlides = this.rpGetSlides();
        if (this.rpSlides.length < 2) return;

        const activeIndex = this.rpSlides.findIndex((slide) => slide.classList.contains('is-active'));
        this.rpIndex = activeIndex >= 0 ? activeIndex : 0;

        // Preload all product media once. After that, switching is immediate.
        this.rpPrepareMedia();
        this.rpShow(this.rpIndex);
        this.rpRestartTimer();

        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.pointerType === 'pen') this.rpTouchStartX = event.clientX;
          clearTimeout(this.rpTimer);
        }, { passive: true });

        this.addEventListener('pointerup', (event) => {
          if (this.rpTouchStartX !== null) {
            const delta = event.clientX - this.rpTouchStartX;
            if (Math.abs(delta) > 45) this.rpShow(this.rpIndex + (delta < 0 ? 1 : -1));
            this.rpTouchStartX = null;
          }
          this.rpRestartTimer();
        }, { passive: true });

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            clearTimeout(this.rpTimer);
            window.pauseAllMedia();
          } else {
            this.rpShow(this.rpIndex);
            this.rpRestartTimer();
          }
        });
      }
    }
  );
}
